import { AppFeaturesKeys } from '@feature-flags';
import { AuthPermission } from '@q6cyber/proto2es/generated/auth';

// Navigate = go to a different phoenix page
// Open = take an action like opening a direct collect
// Change-page = change the current page within the command menu
// None = reserved for root page
export type CommandAction = 'navigate' | 'open' | 'change-page' | 'none';

export interface CommandPage {
  id: string;
  label: string;
  action: CommandAction;
  autocomplete?: (value?: string) => void;
  open?: (value?: string) => void;
  icon?: string;
  shortcut?: string; // single key shortcut
  shortcutLabel?: string;
  placeholder?: string;
  url?: string;
  queryParams?: Record<string, string>;
  permissions?: string[];
  featureFlag?: AppFeaturesKeys | AppFeaturesKeys[];
}

export function getRootPages(
  openDirectCollect: (id?: string) => void,
  getThreatEntities: (searchValue?: string) => void
): Array<CommandPage> {
  return [
    {
      id: 'direct-collect',
      label: 'Direct Collect',
      action: 'open',
      icon: 'view_timeline',
      shortcut: 'D',
      shortcutLabel: '⇧ D',
      placeholder: 'ID or press enter to go to page',
      url: '/direct-collection',
      open: openDirectCollect,
      permissions: [AuthPermission[AuthPermission.DIRECT_COLLECT_VIEW]],
      featureFlag: 'show_direct_collect_viewer',
    },
    {
      id: 'threat-entity',
      label: 'Threat Entity',
      action: 'open',
      icon: 'hub',
      shortcut: 'T',
      shortcutLabel: '⇧ T',
      placeholder: 'Name or press enter to go to page',
      url: '/threat-entity',
      open: getThreatEntities,
      autocomplete: getThreatEntities,
      permissions: [
        'SOME',
        AuthPermission[AuthPermission.THREAT_ENTITY_VIEW],
        AuthPermission[AuthPermission.THREAT_ENTITY_WRITE],
      ],
      featureFlag: 'show_threat_entity',
    },
    {
      id: 'organization',
      label: 'Organization',
      action: 'navigate',
      icon: 'groups',
      shortcut: 'O',
      shortcutLabel: '⇧ O',
      url: '/user-management',
      permissions: [
        'SOME',
        AuthPermission[AuthPermission.TEAM_MANAGER],
        AuthPermission[AuthPermission.TEAM_MANAGER_VIEW],
      ],
      featureFlag: 'show_user_management',
    },
    {
      id: 'client-subscriptions',
      label: 'Client Subscriptions',
      action: 'navigate',
      icon: 'local_offer',
      shortcut: 'S',
      shortcutLabel: '⇧ S',
      url: '/client/subscriptions',
      permissions: [
        'SOME',
        AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_VIEW],
        AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_WRITE],
      ],
      featureFlag: ['show_client_subscriptions', 'show_client_metrics'],
    },
    {
      id: 'client-metrics',
      label: 'Client Metrics',
      action: 'navigate',
      icon: 'trending_up',
      shortcut: 'M',
      shortcutLabel: '⇧ M',
      url: '/client/metrics',
      permissions: [AuthPermission[AuthPermission.CLIENT_METRICS_VIEW]],
      featureFlag: 'show_client_metrics',
    },
    {
      id: 'client-subscription-list',
      label: 'Subscription List',
      action: 'navigate',
      icon: 'list',
      shortcut: 'L',
      shortcutLabel: '⇧ L',
      url: '/client/subscription-list',
      permissions: [
        'SOME',
        AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_VIEW],
        AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_WRITE],
      ],
      featureFlag: 'show_client_subscriptions',
    },
    {
      id: 'bnr',
      label: 'Botnet Reports',
      action: 'navigate',
      icon: 'smart_toy',
      shortcut: 'B',
      shortcutLabel: '⇧ B',
      url: '/botnet-reports',
      permissions: [AuthPermission[AuthPermission.BNR_VIEW]],
      featureFlag: 'show_bnr',
    },
    {
      id: 'infections',
      label: 'Infections',
      action: 'navigate',
      icon: 'warning_amber',
      shortcut: 'I',
      shortcutLabel: '⇧ I',
      url: '/infections',
      permissions: [AuthPermission[AuthPermission.INFECTIONS_VIEW]],
      featureFlag: 'show_infections',
    },
    {
      id: 'fraud-alerts',
      label: 'Fraud Alerts',
      action: 'navigate',
      icon: 'image_search',
      shortcut: 'F',
      shortcutLabel: '⇧ F',
      url: '/fraud-alerts',
      permissions: [
        'SOME',
        AuthPermission[AuthPermission.FRAUD_ALERTS_VIEW],
        AuthPermission[AuthPermission.FRAUD_ALERTS_WRITE],
        AuthPermission[AuthPermission.FRAUD_ALERTS_ADMIN],
      ],
      featureFlag: 'show_fraud_alerts',
    },
  ];
}
